import {
  forwardRef,
  memo,
  useImperativeHandle,
  useState,
  createElement,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Button } from "@/components/ui/button";
import { getMetricFormulaLabel } from "@/screens/utils/Helpers";
import CreativeReportTitle from "./CreativeReportTitle";
import CreativeTable from "./table/CreativeTable";
import ReportLoader from "./ReportLoader";
import NoDataCard from "./NoDataCard";
import { GroupSheet } from "./GroupSheet";
import {
  CreativeReportRow,
  CreativeReportSection as CreativeReportSectionType,
  ReportType,
  CreativeReportData,
  CreativeReportColumn,
  InsightType,
  ViewMode,
  CreativeReportAd,
  ServerPaginationState,
} from "../types";
import { useGlobalStore } from "@/GlobalStore";
import APIErrorCard from "./table/APIErrorCard";
import CreativeCardGrid from "../components/card/CreativeCardGrid";
import { CreativePerformanceGraph } from "./CreativePerformanceGraph";
import { CreativePerformanceBarChart } from "./CreativePerformanceBarChart";
import { CreativeShareChart } from "./CreativeShareChart";
import ReportTitle from "@/screens/analysis/components/report/ReportTitle";
import { creativeSidebarItems } from "./CreativesSidebar";
import { deleteLabelV2 } from "@/screens/creative-label/apiService";
import {
  clearPlatformCache,
  fetchAdDetailData,
  fetchCreativeReportData,
  fetchSectionById,
  getDataConfigurations,
  syncSection,
  updateReportSection,
} from "../apiService";
import ReportInsights from "@/components/common/ReportInsights";
import PageLoader from "@/components/common/loaders/PageLoader";
import ViewModeDropdown from "./configs/ViewModeDropdown";
import Filters, { FiltersCustomTrigger } from "@/components/common/filters/Filters";
import BreakdownBy from "./configs/BreakdownBy";
import SectionLabel from "./configs/SectionLabel";
import LabelView from "./configs/LabelView";
import dropdownDependenciesCheck from "../utils/dropdownDependenciesCheck";
import { useAttachedCreativesStore } from "../store/attachedCreativesStore";
import {
  ColumnOption,
  ConfigurationAdditionalConfig,
  CreativeReportMetadata,
  CreativeSectionConfiguration,
  SectionFilter,
} from "@/types";
import GroupBy, { GroupByOption } from "./configs/GroupBy";
import { useSearchParams } from "react-router-dom";
import Metrics from "@/components/common/metrics";
import EditMetricsToggle from "@/components/common/metrics/components/EditMetricsToggle";
import axios, { CancelTokenSource } from "axios";
import { debounce } from "lodash";
import { useInsights } from "../hooks/useInsights";
import CreativeReportDateSelector from "./CreativeReportDateSelector";
import { TZDate } from "@date-fns/tz";
import { cn } from "@/lib/utils";
import ToastNotification from "@/components/common/toast/ToastNotification";
import ToastContent from "@/components/common/toast/ToastContent";
import { toast } from "sonner";

export interface CreativeReportSectionRef {
  fetchSectionData: (
    sectionId: string,
    configuration: CreativeSectionConfiguration,
    isPolling?: boolean,
    applyToAllSections?: boolean,
  ) => Promise<CreativeReportData | null>;
  getSectionConfiguration: () => CreativeSectionConfiguration;
  setSectionConfiguration: (configuration: CreativeSectionConfiguration) => void;
  getReportSectionData: () => CreativeReportData | null;
  getSectionId: () => string;
  syncSection: () => Promise<void>;
}

interface SectionProps {
  initialSection: CreativeReportSectionType;
  accountId: string | undefined;
  reportId: string | undefined;
  onTitleChange: (
    sectionId: string,
    data: { title?: string; icon?: string },
    changedByUser: boolean,
  ) => void;
  removeSectionOnDeletingLabel: (labelId: string) => void;
  reportType: ReportType;
  isAccountSynced: boolean;
  setIsAutoSaving: (isAutoSaving: boolean) => void;
  checkCache: (
    accountId: string,
    reportId: string,
    sectionId: string,
    configuration: CreativeSectionConfiguration,
  ) => Promise<CreativeReportData | null>;
  saveToCache: (
    accountId: string,
    reportId: string,
    sectionId: string,
    configuration: CreativeSectionConfiguration,
    data: CreativeReportData,
  ) => Promise<void>;
  updateCacheConfiguration: (
    accountId: string,
    reportId: string,
    sectionId: string,
    newConfiguration: CreativeSectionConfiguration,
  ) => Promise<void>;
  allowedDateRange: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  } | null;
  timezone?: string;
  onSectionSyncComplete?: (updatedSection: CreativeReportSectionType) => void;
  onSectionConfigLoaded?: (section: CreativeReportSectionType) => void;
  clearCacheForSection: (
    accountId: string,
    reportId: string,
    sectionId: string,
    configuration: CreativeSectionConfiguration,
  ) => Promise<void>;
  onNewDateAvailable?: () => void;
  onSectionRetryStart?: () => void;
  onSectionRetryComplete?: () => void;
  /** When true, initialSection.configuration is fresh from the server (e.g. just
   *  created via preset) and can be used immediately without waiting for fetchSectionById. */
  configFresh?: boolean;
}

// Custom comparison function to prevent unnecessary re-renders
// Note: Section fetches its own configuration by ID - we don't compare configuration from props
const arePropsEqual = (prevProps: SectionProps, nextProps: SectionProps) => {
  if (prevProps.initialSection.id !== nextProps.initialSection.id) return false;
  if (prevProps.initialSection.title !== nextProps.initialSection.title) return false;
  if (prevProps.initialSection.icon !== nextProps.initialSection.icon) return false;

  if (prevProps.accountId !== nextProps.accountId) return false;
  if (prevProps.reportId !== nextProps.reportId) return false;
  if (prevProps.reportType !== nextProps.reportType) return false;
  if (prevProps.isAccountSynced !== nextProps.isAccountSynced) return false;

  return true;
};

export const CreativeReportSection = memo(
  forwardRef<CreativeReportSectionRef, SectionProps>(
    (
      {
        initialSection,
        accountId,
        reportId,
        onTitleChange,
        removeSectionOnDeletingLabel,
        reportType,
        isAccountSynced,
        checkCache,
        saveToCache,
        setIsAutoSaving,
        updateCacheConfiguration,
        allowedDateRange,
        timezone,
        onSectionSyncComplete,
        onSectionConfigLoaded,
        clearCacheForSection,
        onNewDateAvailable,
        onSectionRetryStart,
        onSectionRetryComplete,
        configFresh,
      },
      ref,
    ) => {
      const isOptimistic = initialSection.id.startsWith("__temp-");
      // Config is trusted when it's an optimistic section (has preset config) or
      // when configFresh is set (section just created, config from server response).
      const isTrustedConfig = isOptimistic || !!configFresh;
      const dataConfigurations = useGlobalStore((state) => state.dataConfigurations);
      const selectedAccount = useGlobalStore((state) => state.selectedAccount);
      const isMergedAccount = selectedAccount?.type === "MERGED";
      const sidebarCollapsed = useGlobalStore((state) => state.sidebarCollapsed);
      const isV2 = selectedAccount?.version === "v2";
      const [reportSectionData, setReportSectionData] =
        useState<CreativeReportData | null>(null);
      const [loadingData, setLoadingData] = useState<boolean>(true);
      const [isViewChanging, setIsViewChanging] = useState(false);
      const [isStale, setIsStale] = useState<boolean>(false);
      const [hasError, setHasError] = useState<boolean>(false);
      const [serverPagination, setServerPagination] = useState<ServerPaginationState>({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
      });
      const serverPaginationRef = useRef(serverPagination);
      const [isPaginationLoading, setIsPaginationLoading] = useState(false);
      const hasV2DataRef = useRef(false);

      const [showDataUpdateNotification, setShowDataUpdateNotification] =
        useState<boolean>(false);
      const [apiDataRef, setApiDataRef] = useState<CreativeReportData | null>(null);
      const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
      const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
      const sectionConfigCancelTokenRef = useRef<CancelTokenSource | null>(null);
      // Show config loader until fetchSectionById returns the authoritative config.
      // Skip the loader when config is trusted (optimistic or just-created sections).
      const [sectionConfigLoading, setSectionConfigLoading] =
        useState<boolean>(!isTrustedConfig);
      // Gate data fetch until fetchSectionById returns the authoritative config.
      // Trusted configs can fetch data immediately.
      const [configFetched, setConfigFetched] = useState<boolean>(isTrustedConfig);
      const [fetchedSection, setFetchedSection] =
        useState<CreativeReportSectionType | null>(null);
      // Use initialSection.configuration when config is trusted (optimistic or just-created).
      // For all other sections, start empty and wait for fetchSectionById to provide
      // the authoritative config — avoids showing stale reportConfig values.
      const [sectionConfiguration, setSectionConfiguration] =
        useState<CreativeSectionConfiguration>(
          isTrustedConfig
            ? initialSection.configuration
            : ({} as CreativeSectionConfiguration),
        );
      const [viewMode, setViewMode] = useState<ViewMode>(
        (initialSection.configuration?.additional_config?.view_mode as ViewMode) ??
          "table_view",
      );
      const setVisibleAdNamesBySection = useGlobalStore(
        (state) => state.setVisibleAdNamesBySection,
      );
      const barChartPageSize = 8;
      const lineChartPageSize = 6;
      const { insightsLoading, insights, generateInsights, clearInsights, setInsights } =
        useInsights();

      const prevInsightsLoadingRef = useRef(false);
      const reportSectionDataRef = useRef(reportSectionData);
      reportSectionDataRef.current = reportSectionData;

      useEffect(() => {
        const wasLoading = prevInsightsLoadingRef.current;
        prevInsightsLoadingRef.current = insightsLoading;

        if (wasLoading && !insightsLoading && insights.length > 0) {
          const currentData = reportSectionDataRef.current;
          if (!currentData?.section) return;

          const insightText = insights.join("\n");
          const updated: CreativeReportData = {
            ...currentData,
            section: {
              ...currentData.section,
              insight: insightText,
            },
          };
          setReportSectionData(updated);

          if (accountId && reportId) {
            void saveToCache(
              accountId,
              reportId,
              initialSection.id,
              sectionConfiguration,
              updated,
            );
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [insightsLoading]);

      const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
      const [advancedTempFilter, setAdvancedTempFilter] = useState<
        SectionFilter | undefined
      >(undefined);
      const advancedOpenedAtRef = useRef<number>(0);
      const [groupSheetOpen, setGroupSheetOpen] = useState(false);
      const [selectedAdGroup, setSelectedAdGroup] = useState<CreativeReportRow | null>(
        null,
      );
      const [groupSheetViewMode, setGroupSheetViewMode] = useState<
        "table_view" | "creative_view" | undefined
      >(undefined);
      const [pendingMessage, setPendingMessage] = useState<string>("Here we go");
      const pendingMessage10sTimeoutRef = useRef<NodeJS.Timeout | null>(null);
      const pendingMessage18sTimeoutRef = useRef<NodeJS.Timeout | null>(null);
      // "Completing" phase: progress bar fills to 100% before data is revealed
      const [loaderCompleting, setLoaderCompleting] = useState(false);
      const prevStatusRef = useRef<string | undefined>(undefined);
      // Ref to track current column_choices order for use in fetchSectionData
      const columnChoicesOrderRef = useRef<Record<string, string[]>>({});
      // Ref to track if we're applying date to all sections to prevent useEffect from triggering
      const isApplyingToAllSectionsRef = useRef<boolean>(false);

      // Mapping object: row[primaryKey] -> row.ads[0].creative
      // where primaryKey is the column key with is_primary === true
      const primaryKeyToCreativeMap = useMemo(() => {
        const map: Record<string, CreativeReportAd> = {};
        if (!reportSectionData?.data?.rows || !reportSectionData?.data?.columns)
          return map;

        // Find the primary key column
        const primaryKeyColumn = reportSectionData.data.columns.find(
          (col) => col.is_primary === true,
        );
        if (!primaryKeyColumn) return map;

        const primaryKey = primaryKeyColumn.key;

        reportSectionData.data.rows.forEach((row) => {
          const primaryKeyValue = (row as Record<string, unknown>)[primaryKey];
          const firstAd = row.ads?.[0] ?? row.first_ad_metadata;

          if (
            firstAd?.creative &&
            primaryKeyValue !== undefined &&
            primaryKeyValue !== null &&
            (typeof primaryKeyValue === "string" || typeof primaryKeyValue === "number")
          ) {
            const key = String(primaryKeyValue);
            map[key] = firstAd;
          }
        });

        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [reportSectionData?.data?.rows]);

      useEffect(() => {
        if (!groupSheetOpen) {
          setSelectedAdGroup(null);
          setGroupSheetViewMode(undefined);
        }
      }, [groupSheetOpen]);

      const handleOpenGroupSheetWithMode = useCallback(
        (row: CreativeReportRow, mode: "table_view" | "creative_view") => {
          setSelectedAdGroup(row);
          setGroupSheetViewMode(mode);
          setGroupSheetOpen(true);
        },
        [],
      );

      // Fetch section configuration by ID - section uses only its own fetched config, not reportConfig
      useEffect(() => {
        // Optimistic sections already have config from props — skip the fetch entirely
        if (initialSection.id.startsWith("__temp-")) {
          setSectionConfigLoading(false);
          return;
        }
        if (
          !accountId ||
          !reportId ||
          !initialSection.id ||
          !isAccountSynced ||
          initialSection.id.startsWith("__temp-")
        ) {
          setSectionConfigLoading(false);
          return;
        }

        // Cancel any existing request
        if (sectionConfigCancelTokenRef.current) {
          sectionConfigCancelTokenRef.current.cancel(
            "Request cancelled due to section change",
          );
        }

        const cancelTokenSource = axios.CancelToken.source();
        sectionConfigCancelTokenRef.current = cancelTokenSource;

        // When config is trusted (e.g. just-created section), don't reset state —
        // the section already has the correct config and can render/fetch data immediately.
        // fetchSectionById still runs in the background to pick up any server-side updates.
        if (!isTrustedConfig) {
          setSectionConfigLoading(true);
          setReportSectionData(null);
          setLoadingData(true);
          setHasError(false);
          setIsStale(false);
          setShowDataUpdateNotification(false);
          setApiDataRef(null);
        }

        fetchSectionById(accountId, reportId, initialSection.id, cancelTokenSource.token)
          .then((section) => {
            setFetchedSection(section);
            setSectionConfiguration(section.configuration);
            setConfigFetched(true);
            setViewMode(
              (section.configuration.additional_config?.view_mode as ViewMode) ??
                "table_view",
            );
            columnChoicesOrderRef.current = section.configuration.column_choices ?? {};
            onSectionConfigLoaded?.(section);
            // Sync title/icon from server if they differ from the stale reportConfig values.
            // Pass changedByUser=true because the server title is authoritative and should
            // always override the local reportConfig value, even if it's a custom title.
            if (
              section.title !== initialSection.title ||
              section.icon !== initialSection.icon
            ) {
              onTitleChange(
                section.id,
                { title: section.title, icon: section.icon ?? undefined },
                true,
              );
            }
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              console.error("Error fetching section configuration:", error);
              setHasError(true);
            }
          })
          .finally(() => {
            setSectionConfigLoading(false);
            sectionConfigCancelTokenRef.current = null;
          });

        return () => {
          if (sectionConfigCancelTokenRef.current) {
            sectionConfigCancelTokenRef.current.cancel(
              "Request cancelled due to section change",
            );
            sectionConfigCancelTokenRef.current = null;
          }
        };
      }, [
        initialSection.id,
        accountId,
        reportId,
        isAccountSynced,
        onSectionConfigLoaded,
      ]);

      // Keep columnChoicesOrderRef in sync with sectionConfiguration
      useEffect(() => {
        columnChoicesOrderRef.current = sectionConfiguration.column_choices ?? {};
      }, [sectionConfiguration.column_choices]);

      // Clear pending message timeouts when status changes from pending
      useEffect(() => {
        if (reportSectionData?.status !== "pending") {
          if (pendingMessage10sTimeoutRef.current) {
            clearTimeout(pendingMessage10sTimeoutRef.current);
            pendingMessage10sTimeoutRef.current = null;
          }
          if (pendingMessage18sTimeoutRef.current) {
            clearTimeout(pendingMessage18sTimeoutRef.current);
            pendingMessage18sTimeoutRef.current = null;
          }
          if (
            reportSectionData?.status === "success" ||
            reportSectionData?.status === "failed"
          ) {
            setPendingMessage("Here we go");
          }
        }
      }, [reportSectionData?.status]);

      const loaderCompletingTimerRef = useRef<NodeJS.Timeout | null>(null);

      /** Call before setReportSectionData when transitioning pending → success
       *  so the 100% animation and data update are batched in the same render. */
      const triggerLoaderCompletion = useCallback(() => {
        if (loaderCompletingTimerRef.current) {
          clearTimeout(loaderCompletingTimerRef.current);
        }
        setLoaderCompleting(true);
        loaderCompletingTimerRef.current = setTimeout(() => {
          setLoaderCompleting(false);
          loaderCompletingTimerRef.current = null;
        }, 600);
      }, []);

      const handleGenerateInsightsForSection = (promptOrId: {
        prompt_id?: string;
        prompt?: string;
      }) => {
        if (!accountId || !reportId) return;
        generateInsights(accountId, reportId, initialSection.id, promptOrId).catch(
          (error) => {
            console.error("Error generating insights:", error);
          },
        );
      };

      useEffect(() => {
        return () => {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel(
              "Operation cancelled due to component unmount",
            );
          }
          if (pendingMessage10sTimeoutRef.current) {
            clearTimeout(pendingMessage10sTimeoutRef.current);
          }
          if (pendingMessage18sTimeoutRef.current) {
            clearTimeout(pendingMessage18sTimeoutRef.current);
          }
          if (loaderCompletingTimerRef.current) {
            clearTimeout(loaderCompletingTimerRef.current);
          }
          clearInsights();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Helper function to reorder columns based on current column_choices order
      const reorderColumnsWithCurrentOrder = useCallback(
        (data: CreativeReportData): CreativeReportData => {
          if (!data?.data?.columns) return data;

          const columnChoices = columnChoicesOrderRef.current;
          if (!columnChoices || Object.keys(columnChoices).length === 0) return data;

          let reorderedColumns = [...data.data.columns];

          // Process each column choice group (e.g., metric, other column types)
          for (const [, desiredOrder] of Object.entries(columnChoices)) {
            if (!desiredOrder || desiredOrder.length === 0) continue;

            // Find columns matching this group
            const groupColumns = reorderedColumns.filter((col) =>
              desiredOrder.includes(col.key),
            );

            // Sort them according to desired order
            groupColumns.sort(
              (a, b) => desiredOrder.indexOf(a.key) - desiredOrder.indexOf(b.key),
            );

            // Find indices of these columns in the current array
            const groupIndices = reorderedColumns
              .map((col, idx) => (desiredOrder.includes(col.key) ? idx : -1))
              .filter((idx) => idx !== -1);

            // Replace at original positions with sorted columns
            const newColumns = [...reorderedColumns];
            groupIndices.forEach((idx, i) => {
              if (i < groupColumns.length) {
                newColumns[idx] = groupColumns[i];
              }
            });
            reorderedColumns = newColumns;
          }

          return {
            ...data,
            data: {
              ...data.data,
              columns: reorderedColumns,
            },
          };
        },
        [],
      );

      const fetchSectionData = useCallback(
        async (
          sectionId: string,
          configuration: CreativeSectionConfiguration,
          isPolling?: boolean,
          applyToAllSections?: boolean,
          skipUpdatingSectionData?: boolean,
          skipPaginationLoader?: boolean,
        ) => {
          if (
            !accountId ||
            !reportId ||
            !configuration.date ||
            sectionId.startsWith("__temp-")
          ) {
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
            }
            return null;
          }

          // Cancel any existing request for this section BEFORE creating a new one
          if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("New request initiated");
            cancelTokenSourceRef.current = null;
          }

          // Create a new cancel token source
          const source = axios.CancelToken.source();

          // Declare cachedData outside try block so it's accessible in catch block
          let cachedData: CreativeReportData | null = null;

          try {
            // Update the cancel token ref with the new source
            cancelTokenSourceRef.current = source;

            // Set loading state (skip when skipping section data updates)
            // For v2: skip full loader when data is already displayed (page/sort changes)
            if (!isPolling && !skipUpdatingSectionData) {
              if (isV2 && hasV2DataRef.current && !skipPaginationLoader) {
                setIsPaginationLoading(true);
              } else if (!isV2 || !hasV2DataRef.current) {
                setLoadingData(true);
              }
            }

            // Check cache first (skip when skipping section data updates, skip for v2 paginated)
            if (!skipUpdatingSectionData && !isV2) {
              try {
                cachedData = await checkCache(
                  accountId,
                  reportId,
                  sectionId,
                  configuration,
                );
                if (cachedData && !isPolling) {
                  setLoadingData(false);
                  setIsStale(false);
                  setHasError(false);
                  // Apply current metric column order to cached data
                  const reorderedCachedData = reorderColumnsWithCurrentOrder(cachedData);
                  setReportSectionData(reorderedCachedData);
                }
              } catch (error) {
                console.error("Error checking cache:", error);
              }
            }

            const v2Params = isV2
              ? {
                  page: serverPaginationRef.current.page,
                  page_size: serverPaginationRef.current.pageSize,
                }
              : {};

            const sectionData = await fetchCreativeReportData({
              accountId,
              sectionId,
              sectionData: {
                ...configuration,
              },
              cancelToken: source.token,
              isPolling,
              video_retention: true,
              use_new_breakdown_structure: true,
              section_level_date: true,
              ...(applyToAllSections === true && { apply_to_all_sections: true }),
              ...v2Params,
            });

            // Always set loading to false when a request completes successfully (skip when skipping section data updates)
            if (!skipUpdatingSectionData) {
              setLoadingData(false);
              setIsPaginationLoading(false);
            }

            if (!sectionData) {
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
              }
              throw new Error("No data received from API");
            }

            const status = sectionData?.status || "success";

            // Update server pagination state for v2
            if (isV2 && sectionData.pagination) {
              const paginationUpdate = {
                page: sectionData.pagination.page,
                pageSize: sectionData.pagination.page_size,
                totalCount: sectionData.pagination.total_count,
                totalPages: sectionData.pagination.total_pages,
              };
              setServerPagination(paginationUpdate);
              serverPaginationRef.current = paginationUpdate;
            }

            if (isV2 && sectionData.data) {
              hasV2DataRef.current = true;
            }

            if (!skipUpdatingSectionData) {
              // Clear any existing timeout
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
              }

              // Trigger progress → 100% animation when pending → success
              // This is batched with the setReportSectionData call below,
              // so the loader stays visible during the same render.
              if (prevStatusRef.current === "pending" && status === "success") {
                triggerLoaderCompletion();
              }
              prevStatusRef.current = status;

              // Store API data for comparison
              const reorderedApiData = reorderColumnsWithCurrentOrder(sectionData);

              // If cached data exists, handle comparison or error notification
              if (cachedData) {
                if (status === "success") {
                  // Compare data to check if it changed
                  const previousRowsForComparison = cachedData.data?.rows?.map((row) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { ads, ...rowWithoutAds } = row;
                    return rowWithoutAds;
                  });

                  const newRowsForComparison = sectionData.data?.rows?.map((row) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { ads, ...rowWithoutAds } = row;
                    return rowWithoutAds;
                  });

                  const isDataDifferent =
                    JSON.stringify(previousRowsForComparison) !==
                    JSON.stringify(newRowsForComparison);

                  // Show notification if data has changed
                  if (isDataDifferent) {
                    setApiDataRef(reorderedApiData);
                    setShowDataUpdateNotification(true);
                  } else {
                    // Data is the same, update displayed data with latest API data
                    setReportSectionData(reorderedApiData);
                  }
                } else if (status === "failed") {
                  // API failed but we have cached data - show notification so user can sync
                  setApiDataRef(reorderedApiData);
                  setShowDataUpdateNotification(true);
                  // Keep showing cached data, don't update to error state yet
                }
                // If status is pending/partial, keep showing cached data (already displayed above)
              } else if (!cachedData) {
                // No cached data, update with API data immediately
                setReportSectionData(reorderedApiData);
                if (status === "failed") {
                  // No cached data and API failed - show error state
                  setIsStale(false);
                  setHasError(true);
                } else {
                  setIsStale(false);
                  setHasError(false);
                }
              }

              // Set up polling if needed
              if (status === "pending" || status === "partial") {
                const timeoutId = setTimeout(() => {
                  void fetchSectionData(
                    sectionId,
                    configuration,
                    true,
                    undefined,
                    skipUpdatingSectionData,
                  );
                }, 5000);

                pollingTimeoutRef.current = timeoutId;

                // Set up pending message timeouts when status is pending
                // Reset timers if a new message comes from the API
                if (status === "pending") {
                  // Clear any existing timeouts first
                  if (pendingMessage10sTimeoutRef.current) {
                    clearTimeout(pendingMessage10sTimeoutRef.current);
                    pendingMessage10sTimeoutRef.current = null;
                  }
                  if (pendingMessage18sTimeoutRef.current) {
                    clearTimeout(pendingMessage18sTimeoutRef.current);
                    pendingMessage18sTimeoutRef.current = null;
                  }

                  // Set message from API (or default)
                  const apiMessage = sectionData?.message ?? "Here we go";
                  setPendingMessage(apiMessage);

                  if (!isMergedAccount) {
                    // Set timeout for 10 seconds
                    pendingMessage10sTimeoutRef.current = setTimeout(() => {
                      setPendingMessage("All set. Loading your view…");
                    }, 10000);

                    // Set timeout for 18 seconds
                    pendingMessage18sTimeoutRef.current = setTimeout(() => {
                      setPendingMessage("Slow lane today. We’ll land this shortly.");
                    }, 18000);
                  }
                }
              } else {
                // Clear pending message timeouts when status is no longer pending
                if (pendingMessage10sTimeoutRef.current) {
                  clearTimeout(pendingMessage10sTimeoutRef.current);
                  pendingMessage10sTimeoutRef.current = null;
                }
                if (pendingMessage18sTimeoutRef.current) {
                  clearTimeout(pendingMessage18sTimeoutRef.current);
                  pendingMessage18sTimeoutRef.current = null;
                }
                setPendingMessage("Here we go");
              }
            }

            // Cache the data for successful requests (skip caching for v2 paginated responses)
            if (status === "success" && !isV2) {
              try {
                await saveToCache(
                  accountId,
                  reportId,
                  sectionId,
                  configuration,
                  sectionData,
                );
                if (!skipUpdatingSectionData && sectionData.section) {
                  onSectionSyncComplete?.(sectionData.section);
                }
              } catch (error) {
                console.error("Error saving to cache:", error);
              }
            } else if (status === "success" && isV2) {
              if (!skipUpdatingSectionData && sectionData.section) {
                onSectionSyncComplete?.(sectionData.section);
              }
            }

            return sectionData;
          } catch (error) {
            if (!axios.isCancel(error)) {
              // Only set loading to false if it's not a cancelled request (and not skipping section data updates)
              if (!skipUpdatingSectionData) {
                setLoadingData(false);
                setIsPaginationLoading(false);
              }
              console.error("Error fetching section data:", error);

              // If we have cached data, show notification instead of error state
              if (cachedData) {
                // Create error response data structure; preserve error_type from API when available (e.g. axios error.response.data)
                const responseData =
                  axios.isAxiosError(error) && error.response?.data
                    ? (error.response.data as Partial<CreativeReportData>)
                    : undefined;
                const errorData: CreativeReportData = {
                  status: "failed",
                  message:
                    error instanceof Error ? error.message : "Failed to fetch data",
                  ...(responseData?.error_type && {
                    error_type: responseData.error_type,
                  }),
                  ...(responseData?.message_title && {
                    message_title: responseData.message_title,
                  }),
                };
                setApiDataRef(errorData);
                setShowDataUpdateNotification(true);
                // Keep showing cached data, don't update to error state yet
                setHasError(false);
                setIsStale(false);
              } else {
                // No cached data, show error state immediately
                if (!skipUpdatingSectionData) {
                  setHasError(true);
                  setIsStale(false);
                }
              }
            }
            return null;
          } finally {
            // Only clear the ref if this is still the current request
            // (to avoid clearing a newer request's cancel token)
            if (cancelTokenSourceRef.current === source) {
              cancelTokenSourceRef.current = null;
            }
            // Reset the apply to all sections ref after fetch completes
            if (applyToAllSections === true) {
              isApplyingToAllSectionsRef.current = false;
            }
          }
        },
        [
          accountId,
          reportId,
          reorderColumnsWithCurrentOrder,
          checkCache,
          saveToCache,
          onSectionSyncComplete,
          isV2,
          isMergedAccount,
          triggerLoaderCompletion,
        ],
      );

      // Handle section sync
      const handleSyncSection = useCallback(async () => {
        if (!accountId || !reportId || !initialSection.id) {
          toast.message(
            <ToastContent
              headingPrefix="An error occurred!"
              subheadingSuffix="Unable to sync section. Missing required data."
              isError={true}
            />,
          );
          return;
        }

        try {
          const res = await syncSection(accountId, initialSection.id);
          if (res.status === "failed") {
            toast.message(
              <ToastContent
                headingPrefix="An error occurred!"
                subheadingSuffix="Failed to sync section. Please try again."
                isError={true}
              />,
            );
            return;
          }

          // On syncing no configuration changes happening only data may change
          // Clear cache for this section since data has been synced
          try {
            await clearCacheForSection(
              accountId,
              reportId,
              initialSection.id,
              sectionConfiguration,
            );
          } catch (error) {
            console.error("Error clearing cache for section:", error);
          }

          // Fetch updated section data (use fetched config, not reportConfig)
          const updatedSection = await fetchSectionById(
            accountId,
            reportId,
            initialSection.id,
          );

          setFetchedSection(updatedSection);
          setSectionConfiguration(updatedSection.configuration);

          // Notify parent component about sync completion
          if (onSectionSyncComplete) {
            onSectionSyncComplete(updatedSection);
          }

          // Reset to page 1 for v2 on sync
          if (isV2) {
            const resetPagination = { ...serverPaginationRef.current, page: 1 };
            setServerPagination(resetPagination);
            serverPaginationRef.current = resetPagination;
          }

          // Trigger refetch for this section with fresh config
          await fetchSectionData(initialSection.id, updatedSection.configuration, false);
        } catch (error) {
          console.error("Error syncing section:", error);
          toast.message(
            <ToastContent
              headingPrefix="An error occurred!"
              subheadingSuffix="Failed to sync section. Please try again."
              isError={true}
            />,
          );
        }
      }, [
        accountId,
        reportId,
        initialSection.id,
        sectionConfiguration,
        clearCacheForSection,
        onSectionSyncComplete,
        fetchSectionData,
        isV2,
      ]);

      // Expose methods and state to parent via ref
      useImperativeHandle(ref, () => ({
        fetchSectionData,
        getSectionConfiguration: () => sectionConfiguration,
        setSectionConfiguration: (configuration: CreativeSectionConfiguration) =>
          setSectionConfiguration(configuration),
        getReportSectionData: () => reportSectionData,
        getSectionId: () => initialSection.id,
        syncSection: handleSyncSection,
      }));

      // Serialize date to avoid re-fetching when fetchSectionById returns a new
      // config object with the same date values (prevents double data load on section switch)
      const serializedDate = useMemo(
        () => JSON.stringify(sectionConfiguration.date),
        [sectionConfiguration.date],
      );

      useEffect(() => {
        // Optimistic sections don't have a real backend ID yet — skip data fetch
        if (initialSection.id.startsWith("__temp-")) return;
        // Wait for fetchSectionById to return the authoritative config before
        // fetching data. This prevents fetching with stale config from reportConfig
        // (e.g., wrong date range) when switching between sections.
        if (!configFetched) return;
        if (isAccountSynced && sectionConfiguration.date) {
          // Skip automatic fetch if we're in the middle of applying to all sections
          // (handleConfigurationChange will handle the fetch with the flag)
          if (isApplyingToAllSectionsRef.current) {
            isApplyingToAllSectionsRef.current = false;
            return;
          }
          // Cancel any pending requests before fetching new data
          if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("Date or sync status changed");
            cancelTokenSourceRef.current = null;
          }
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          // Reset to page 1 for v2 when date or sync status changes
          if (isV2) {
            const resetPagination = { ...serverPaginationRef.current, page: 1 };
            setServerPagination(resetPagination);
            serverPaginationRef.current = resetPagination;
          }
          void fetchSectionData(initialSection.id, sectionConfiguration, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isAccountSynced, serializedDate, configFetched]);

      const onRetry = () => {
        // Use error_type only from fresh API data (apiDataRef)
        const errorTypeForRetry =
          apiDataRef?.status === "failed"
            ? apiDataRef?.error_type
            : reportSectionData?.error_type;
        setShowDataUpdateNotification(false);
        setApiDataRef(null);
        setHasError(false);
        // Clear displayed data so we show loading and then latest state from API (not cache)
        setReportSectionData(null);
        setLoadingData(true);
        // Reset to page 1 for v2 on retry
        if (isV2) {
          const resetPagination = { ...serverPaginationRef.current, page: 1 };
          setServerPagination(resetPagination);
          serverPaginationRef.current = resetPagination;
          hasV2DataRef.current = false;
        }
        // Cancel any pending requests before retrying
        if (cancelTokenSourceRef.current) {
          cancelTokenSourceRef.current.cancel("Retry initiated");
          cancelTokenSourceRef.current = null;
        }
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        onSectionRetryStart?.();
        const retry = async () => {
          try {
            // Clear section cache so fetch returns latest state instead of reusing cache
            if (accountId && reportId && clearCacheForSection) {
              try {
                await clearCacheForSection(
                  accountId,
                  reportId,
                  initialSection.id,
                  sectionConfiguration,
                );
              } catch (error) {
                console.error("Error clearing cache for section during retry:", error);
              }
            }
            if (accountId && errorTypeForRetry === "retry") {
              try {
                await clearPlatformCache(accountId, {
                  ...sectionConfiguration,
                });
              } catch (error) {
                console.error("Error clearing platform cache during retry:", error);
              }
            }
            await fetchSectionData(initialSection.id, sectionConfiguration, false);
          } finally {
            onSectionRetryComplete?.();
          }
        };
        void retry();
      };

      const handleConfigurationChange = useCallback(
        (
          partialConfig: Partial<CreativeSectionConfiguration>,
          keyChanged?: keyof CreativeSectionConfiguration,
          applyToAllSections?: boolean,
        ) => {
          // Set selected section to trigger the effect
          const previousConfig = sectionConfiguration;

          // Merge partial config with current configuration
          const configuration: CreativeSectionConfiguration = {
            ...sectionConfiguration,
            ...partialConfig,
          };

          // Handle group_by change for title update
          // Pass changedByUser=true because the user explicitly changed the group_by,
          // so the title should always update to match — even over a custom title.
          if (
            keyChanged === "dropdowns" &&
            previousConfig?.dropdowns?.["group_by"] !==
              configuration.dropdowns?.["group_by"]
          ) {
            // used ref for latest value of dataConfig
            // it was forming closure for Label Dialog prop setSelectedFilter
            const newTitle = dataConfigurations?.dropdowns
              ?.find((d) => d.key === "group_by")
              ?.options?.find(
                (choice) => choice.key === configuration?.dropdowns?.["group_by"],
              )?.label;
            onTitleChange(initialSection.id, { title: newTitle }, true);
          }

          // Original fetch logic for other configuration changes
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }

          // Cancel any pending request for this section
          if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("Configuration changed");
            cancelTokenSourceRef.current = null;
          }

          // Check if only breakdown_by changed
          const isOnlyBreakdownChanged =
            keyChanged === "dropdowns" &&
            previousConfig?.dropdowns?.["breakdown_by"] !==
              configuration.dropdowns?.["breakdown_by"] &&
            // Ensure no other dropdown values changed
            previousConfig?.dropdowns?.["group_by"] ===
              configuration.dropdowns?.["group_by"] &&
            previousConfig?.dropdowns?.["label_view"] ===
              configuration.dropdowns?.["label_view"] &&
            // Ensure other configs didn't change
            JSON.stringify(previousConfig?.filters) ===
              JSON.stringify(configuration.filters) &&
            JSON.stringify(previousConfig?.column_choices) ===
              JSON.stringify(configuration.column_choices) &&
            previousConfig?.date?.start_date === configuration.date?.start_date &&
            previousConfig?.date?.end_date === configuration.date?.end_date;

          // Check if only compare_date changed (date range unchanged)
          const isOnlyCompareDateChanged =
            keyChanged === undefined &&
            previousConfig?.date?.start_date === configuration.date?.start_date &&
            previousConfig?.date?.end_date === configuration.date?.end_date &&
            JSON.stringify(previousConfig?.compare_date) !==
              JSON.stringify(configuration.compare_date) &&
            JSON.stringify(previousConfig?.dropdowns) ===
              JSON.stringify(configuration.dropdowns) &&
            JSON.stringify(previousConfig?.filters) ===
              JSON.stringify(configuration.filters) &&
            JSON.stringify(previousConfig?.column_choices) ===
              JSON.stringify(configuration.column_choices);

          // Check if only columns changed
          const isOnlyColumnsChanged =
            keyChanged === "column_choices" &&
            !!previousConfig?.column_choices &&
            !!configuration.column_choices;

          if (isOnlyBreakdownChanged) {
            setReportSectionData((prevData) => {
              if (!prevData) return null;

              return {
                ...prevData,
                status:
                  !loadingData && prevData.status !== "pending"
                    ? "partial"
                    : prevData.status,
                data: prevData.data && {
                  ...prevData.data,
                  rows: prevData.data.rows.map((row) => ({
                    ...row,
                    breakdowns: undefined, // Clear breakdowns to trigger loading shimmer
                  })),
                },
              };
            });
          } else if (isOnlyCompareDateChanged) {
            // Check if compare_date is being removed (set to null/undefined) or just changed
            const isCompareDateRemoved =
              !configuration.compare_date &&
              previousConfig?.compare_date &&
              (previousConfig.compare_date.start_date ||
                previousConfig.compare_date.end_date);

            const metricKeys = configuration.column_choices?.metric ?? [];
            setReportSectionData((prevData) => {
              if (!prevData?.data) return prevData;

              // Include winning criteria columns (they also get _previous / _change / _percent_change when compare is enabled)
              const winningColumnKeys = (prevData.data.columns || [])
                .filter((col: CreativeReportColumn) =>
                  String(col.key).includes("winning"),
                )
                .map((col: CreativeReportColumn) => col.key);
              const allKeysToUpdate = [...new Set([...metricKeys, ...winningColumnKeys])];

              if (isCompareDateRemoved) {
                // Remove compare date keys when compare is removed
                const rows = prevData.data.rows.map((row) => {
                  const r = { ...row } as Record<string, unknown>;
                  allKeysToUpdate.forEach((k) => {
                    r[`${k}_previous`] = "N/A";
                    r[`${k}_change`] = "N/A";
                    r[`${k}_percent_change`] = undefined;
                  });
                  r.total_ads_previous = "N/A";
                  r.total_ads_change = "N/A";
                  delete r.ads_previous;
                  return r as CreativeReportRow;
                });

                const total = prevData.data.total as Record<string, unknown>;
                const updatedTotal = total
                  ? (() => {
                      const t = { ...total };
                      allKeysToUpdate.forEach((k) => {
                        t[`${k}_previous`] = "N/A";
                        t[`${k}_change`] = "N/A";
                        t[`${k}_percent_change`] = undefined;
                      });
                      t.total_ads_previous = "N/A";
                      t.total_ads_change = "N/A";
                      delete t.ads_previous;
                      return t;
                    })()
                  : prevData.data.total;

                return {
                  ...prevData,
                  status:
                    !loadingData && prevData.status !== "pending"
                      ? "partial"
                      : prevData.status,
                  data: {
                    ...prevData.data,
                    rows,
                    total: updatedTotal,
                  },
                };
              } else {
                // Set status to partial and show loader for change/previous values when compare date changes
                const loadingSentinel = "__LOADING__";
                const rows = prevData.data.rows.map((row) => {
                  const r = { ...row } as Record<string, unknown>;
                  allKeysToUpdate.forEach((k) => {
                    r[`${k}_previous`] = loadingSentinel;
                    r[`${k}_change`] = loadingSentinel;
                    r[`${k}_percent_change`] = loadingSentinel;
                  });
                  r.total_ads_previous = loadingSentinel;
                  r.total_ads_change = loadingSentinel;
                  return r as CreativeReportRow;
                });

                const total = prevData.data.total as Record<string, unknown>;
                const updatedTotal = total
                  ? (() => {
                      const t = { ...total };
                      allKeysToUpdate.forEach((k) => {
                        t[`${k}_previous`] = loadingSentinel;
                        t[`${k}_change`] = loadingSentinel;
                        t[`${k}_percent_change`] = loadingSentinel;
                      });
                      t.total_ads_previous = loadingSentinel;
                      t.total_ads_change = loadingSentinel;
                      return t;
                    })()
                  : prevData.data.total;

                return {
                  ...prevData,
                  status:
                    !loadingData && prevData.status !== "pending"
                      ? "partial"
                      : prevData.status,
                  data: {
                    ...prevData.data,
                    rows,
                    total: updatedTotal,
                  },
                };
              }
            });
          } else if (isOnlyColumnsChanged) {
            // Find the changed column group and the specific column
            let addedColumn: { group: string; column: string } | null = null;
            let removedColumn: { group: string; column: string } | null = null;

            // Since we know column_choices is an object with arrays
            for (const [group, columns] of Object.entries(
              configuration.column_choices || {},
            )) {
              const prevColumns = previousConfig?.column_choices?.[group] || [];
              // Find added column
              const added = columns.find((col) => !prevColumns.includes(col));
              if (added) {
                addedColumn = { group, column: added };
              }
              // Find removed column
              const removed = prevColumns.find((col) => !columns.includes(col));
              if (removed) {
                removedColumn = { group, column: removed };
              }
            }

            setReportSectionData((prevData) => {
              if (!prevData) return null;

              return {
                ...prevData,
                status:
                  !loadingData && prevData.status !== "pending"
                    ? "partial"
                    : prevData.status,

                data: prevData.data && {
                  ...prevData.data,
                  columns: (() => {
                    // First handle added/removed columns
                    let updatedColumns = addedColumn
                      ? (() => {
                          const newColumn = {
                            key: addedColumn.column,
                            label: (() => {
                              const columnGroup = dataConfigurations?.column_choices.find(
                                (choice) => choice.key === addedColumn?.group,
                              );
                              return (
                                columnGroup?.options.find(
                                  (opt) => opt.key === addedColumn?.column,
                                )?.label || addedColumn.column
                              );
                            })(),
                            column_type: (() => {
                              const columnGroup = dataConfigurations?.column_choices.find(
                                (choice) => choice.key === addedColumn?.group,
                              );
                              return columnGroup?.column_type || "text";
                            })(),
                          };

                          const currentColumns = [...(prevData.data?.columns || [])];
                          const columnType = newColumn.column_type;

                          if (
                            columnType === "text_ai_label" ||
                            columnType === "text_ai_description"
                          ) {
                            // Find the last primary column index
                            const lastPrimaryIndex =
                              currentColumns.length -
                              1 -
                              [...currentColumns]
                                .reverse()
                                .findIndex(
                                  (col: CreativeReportColumn) => col.is_primary === true,
                                );

                            if (columnType === "text_ai_label") {
                              // Find the last text_ai_label column index
                              const lastAiLabelIndex =
                                currentColumns.length -
                                1 -
                                [...currentColumns]
                                  .reverse()
                                  .findIndex(
                                    (col: CreativeReportColumn) =>
                                      col.column_type === "text_ai_label",
                                  );

                              const insertIndex =
                                lastAiLabelIndex !== currentColumns.length
                                  ? lastAiLabelIndex + 1
                                  : lastPrimaryIndex + 1;

                              currentColumns.splice(insertIndex, 0, newColumn);
                            } else {
                              // For text_ai_description, insert after primary columns
                              currentColumns.splice(lastPrimaryIndex + 1, 0, newColumn);
                            }
                          } else if (columnType === "number") {
                            // Find the index of first winning column
                            const winningIndex = currentColumns.findIndex((col) =>
                              col.key.includes("winning"),
                            );

                            if (winningIndex !== -1) {
                              currentColumns.splice(winningIndex, 0, newColumn);
                            } else {
                              currentColumns.push(newColumn);
                            }
                          } else {
                            currentColumns.push(newColumn);
                          }

                          return currentColumns;
                        })()
                      : removedColumn
                        ? prevData.data?.columns?.filter(
                            (col) => col.key !== removedColumn?.column,
                          )
                        : prevData.data?.columns;

                    // updating order of metrics
                    const desiredOrderOfMetrics =
                      configuration.column_choices?.["metric"] ?? [];
                    // Create a new array with the correct order
                    const metricColumns = updatedColumns?.filter((col) =>
                      desiredOrderOfMetrics.includes(col.key),
                    );
                    // Sort metric columns according to desired order
                    metricColumns.sort(
                      (a, b) =>
                        desiredOrderOfMetrics.indexOf(a.key) -
                        desiredOrderOfMetrics.indexOf(b.key),
                    );
                    // Find the position where metrics should be inserted
                    // For simplicity, we'll keep non-metrics in their original positions
                    // and replace the original metrics with the sorted ones
                    const metricIndices = updatedColumns
                      .map((col, idx) =>
                        desiredOrderOfMetrics.includes(col.key) ? idx : -1,
                      )
                      .filter((idx) => idx !== -1);

                    // Create the final array with correct ordering
                    const reorderedColumns = [...updatedColumns];
                    metricIndices.forEach((idx, i) => {
                      if (i < metricColumns.length) {
                        reorderedColumns[idx] = metricColumns[i];
                      }
                    });
                    // Update the columns with the reordered array
                    updatedColumns = reorderedColumns;
                    return updatedColumns;
                  })(),
                },
              };
            });
          } else {
            setReportSectionData((prevData) => {
              if (!prevData) return null;

              return {
                ...prevData,
                status: "pending",
              };
            });
          }

          // Detect case: only a metric column was removed (no additions, no other group changes)
          const metricRemovalOnly =
            isOnlyColumnsChanged &&
            (() => {
              const prevMetric = previousConfig.column_choices?.metric ?? [];
              const nextMetric = configuration.column_choices?.metric ?? [];
              return (
                prevMetric.length === nextMetric.length + 1 &&
                nextMetric.every((m) => prevMetric.includes(m))
              );
            })();

          // Set ref to prevent useEffect from triggering when applying to all sections
          if (applyToAllSections) {
            isApplyingToAllSectionsRef.current = true;
          }

          // Update only the specific key that changed to prevent unnecessary re-renders
          if (keyChanged) {
            setSectionConfiguration((prev) => ({
              ...prev,
              [keyChanged]: partialConfig[keyChanged],
            }));
          } else {
            // If no key specified, update the entire config (backwards compatibility)
            setSectionConfiguration(configuration);
          }

          // Update the ref synchronously to prevent race conditions
          // This ensures fetchSectionData uses the latest column order even if API returns quickly
          if (keyChanged === "column_choices" && configuration.column_choices) {
            columnChoicesOrderRef.current = configuration.column_choices;
          }

          // Reset to page 1 for v2 when config changes (filter, groupby, etc.)
          if (isV2) {
            const resetPagination = { ...serverPaginationRef.current, page: 1 };
            setServerPagination(resetPagination);
            serverPaginationRef.current = resetPagination;
          }

          // Fetch new data with updated configuration
          void fetchSectionData(
            initialSection.id,
            configuration,
            false,
            applyToAllSections,
            metricRemovalOnly && !loadingData, // Skip updating section data when only a metric is removed
            isOnlyBreakdownChanged || isOnlyColumnsChanged, // Breakdown/column changes have their own shimmer
          );
        },
        [
          sectionConfiguration,
          dataConfigurations,
          onTitleChange,
          initialSection.id,
          fetchSectionData,
          pollingTimeoutRef,
          cancelTokenSourceRef,
          setReportSectionData,
          loadingData,
          setSectionConfiguration,
          isV2,
        ],
      );

      const debouncedUpdateConfiguration = useMemo(
        () =>
          debounce((sectionId: string, configuration: CreativeSectionConfiguration) => {
            if (accountId && reportId && !sectionId.startsWith("__temp-")) {
              setIsAutoSaving(true);
              void updateReportSection(accountId, reportId, sectionId, {
                configuration,
              }).finally(() => {
                setIsAutoSaving(false);
              });
            }
          }, 1000),
        [accountId, reportId, setIsAutoSaving],
      );

      // Update configuration without refetching data (for reorders)
      const handleConfigOnlyUpdate = useCallback(
        (configuration: CreativeSectionConfiguration) => {
          // Update local configuration state first
          setSectionConfiguration(configuration);

          // Update the ref synchronously to prevent race conditions with pending API calls
          if (configuration.column_choices) {
            columnChoicesOrderRef.current = configuration.column_choices;
          }

          // Update sectionWiseData with new column order
          setReportSectionData((prevData) => {
            if (!prevData) return null;

            return {
              ...prevData,
              data: prevData.data && {
                ...prevData.data,
                columns: (() => {
                  // Reorder columns based on new metric order
                  const desiredOrderOfMetrics =
                    configuration.column_choices?.["metric"] ?? [];
                  const updatedColumns = [...(prevData.data?.columns || [])];

                  const metricColumns = updatedColumns.filter((col) =>
                    desiredOrderOfMetrics.includes(col.key),
                  );
                  metricColumns.sort(
                    (a, b) =>
                      desiredOrderOfMetrics.indexOf(a.key) -
                      desiredOrderOfMetrics.indexOf(b.key),
                  );

                  const metricIndices = updatedColumns
                    .map((col, idx) =>
                      desiredOrderOfMetrics.includes(col.key) ? idx : -1,
                    )
                    .filter((idx) => idx !== -1);

                  const reorderedColumns = [...updatedColumns];
                  metricIndices.forEach((idx, i) => {
                    if (i < metricColumns.length) {
                      reorderedColumns[idx] = metricColumns[i];
                    }
                  });

                  return reorderedColumns;
                })(),
              },
            };
          });

          // Update cache configuration to keep column order in sync
          if (accountId && reportId) {
            updateCacheConfiguration(
              accountId,
              reportId,
              initialSection.id,
              configuration,
            ).catch((error) => {
              console.error(
                "Error updating cache configuration for column order:",
                error,
              );
            });
          }

          // Save to backend
          debouncedUpdateConfiguration(initialSection.id, configuration);
        },
        [
          debouncedUpdateConfiguration,
          initialSection.id,
          accountId,
          reportId,
          updateCacheConfiguration,
        ],
      );

      const handleUpdateAdditionalConfig = useCallback(
        (additionalConfig: Partial<ConfigurationAdditionalConfig>) => {
          if (!accountId || !reportId || initialSection.id.startsWith("__temp-")) return;

          const updatedConfiguration: CreativeSectionConfiguration = {
            ...sectionConfiguration,
            additional_config: {
              ...sectionConfiguration.additional_config,
              ...additionalConfig,
            },
          };
          setSectionConfiguration((prev) => ({
            ...prev,
            additional_config: {
              ...prev.additional_config,
              ...additionalConfig,
            },
          }));

          // Update cache configuration to keep it in sync
          updateCacheConfiguration(
            accountId,
            reportId,
            initialSection.id,
            updatedConfiguration,
          ).catch((error) => {
            console.error("Error updating cache configuration:", error);
          });

          setIsAutoSaving(true);
          updateReportSection(accountId, reportId, initialSection.id, {
            configuration: updatedConfiguration,
          })
            .catch((error) => {
              console.error("Error in updating additional config:", error);
            })
            .finally(() => {
              setIsAutoSaving(false);
            });
        },
        [
          accountId,
          reportId,
          sectionConfiguration,
          updateCacheConfiguration,
          initialSection.id,
          setIsAutoSaving,
        ],
      );

      const handleGraphConfigChange = useCallback(
        (graphConfig: ConfigurationAdditionalConfig["graph_config"]) => {
          if (!graphConfig) return;
          handleUpdateAdditionalConfig({
            graph_config: graphConfig,
          });
        },
        [handleUpdateAdditionalConfig],
      );

      // Handler for bar chart config change (supports multiple metrics)
      const handleBarConfigChange = useCallback(
        (config: { metric?: string | null; metrics?: string[] }) => {
          const existingConfig = sectionConfiguration.additional_config?.graph_config;
          handleUpdateAdditionalConfig({
            graph_config: {
              interval: existingConfig?.interval || "weekly", // Ensure interval is always present
              // Support both old single metric and new multi-metric format
              ...(config.metrics !== undefined
                ? { metrics: config.metrics }
                : config.metric !== undefined
                  ? { metric: config.metric }
                  : {}),
            },
          });
        },
        [
          handleUpdateAdditionalConfig,
          sectionConfiguration.additional_config?.graph_config,
        ],
      );

      const handleColumnResize = useCallback(
        (columnSizing: Record<string, number>) => {
          if (!columnSizing) return;
          handleUpdateAdditionalConfig({
            column_configuration: columnSizing,
          });
        },
        [handleUpdateAdditionalConfig],
      );

      const handleColumnSort = useCallback(
        (sortState: ConfigurationAdditionalConfig["sort_state"]) => {
          if (!sortState) return;
          handleUpdateAdditionalConfig({
            sort_state: sortState,
          });

          if (isV2) {
            // Reset to page 1 on sort change and re-fetch with updated sort in config
            const resetPagination = { ...serverPaginationRef.current, page: 1 };
            setServerPagination(resetPagination);
            serverPaginationRef.current = resetPagination;
            const updatedConfig: CreativeSectionConfiguration = {
              ...sectionConfiguration,
              additional_config: {
                ...sectionConfiguration.additional_config,
                sort_state: sortState,
              },
            };
            void fetchSectionData(initialSection.id, updatedConfig, false);
          }
        },
        [
          handleUpdateAdditionalConfig,
          isV2,
          sectionConfiguration,
          fetchSectionData,
          initialSection.id,
        ],
      );

      const handleViewMode = (mode: ViewMode) => {
        setViewMode(mode);
        setVisibleAdNamesBySection(initialSection.id || "", []);

        handleUpdateAdditionalConfig({
          view_mode: mode as ConfigurationAdditionalConfig["view_mode"],
        });

        // v2: adjust server page size per view and re-fetch
        if (isV2) {
          setIsViewChanging(true);
          const targetPageSize =
            mode === "bar_chart_view"
              ? barChartPageSize
              : mode === "line_chart_view"
                ? lineChartPageSize
                : 10;
          const updated = {
            ...serverPaginationRef.current,
            pageSize: targetPageSize,
            page: 1,
          };
          setServerPagination(updated);
          serverPaginationRef.current = updated;
          const updatedConfig: CreativeSectionConfiguration = {
            ...sectionConfiguration,
            additional_config: {
              ...sectionConfiguration.additional_config,
              view_mode: mode as ConfigurationAdditionalConfig["view_mode"],
            },
          };
          fetchSectionData(
            initialSection.id,
            updatedConfig,
            false,
            undefined,
            undefined,
            true,
          ).finally(() => setIsViewChanging(false));
        }
      };

      const handleServerPageChange = useCallback(
        (page: number) => {
          if (!isV2) return;
          const updated = { ...serverPaginationRef.current, page };
          setServerPagination(updated);
          serverPaginationRef.current = updated;
          void fetchSectionData(initialSection.id, sectionConfiguration, false);
        },
        [isV2, fetchSectionData, initialSection.id, sectionConfiguration],
      );

      const handleServerPageSizeChange = useCallback(
        (pageSize: number) => {
          if (!isV2) return;
          const updated = { ...serverPaginationRef.current, pageSize, page: 1 };
          setServerPagination(updated);
          serverPaginationRef.current = updated;
          void fetchSectionData(initialSection.id, sectionConfiguration, false);
        },
        [isV2, fetchSectionData, initialSection.id, sectionConfiguration],
      );

      // v2: fetch a page of ads for the gallery infinite scroll
      const handleFetchGalleryAds = useCallback(
        async (groupValue: string, page: number, pageSize: number) => {
          if (!isV2 || !accountId) return null;
          try {
            const response = await fetchAdDetailData({
              accountId,
              sectionId: initialSection.id,
              sectionData: sectionConfiguration,
              group_value: groupValue,
              ad_page: page,
              ad_page_size: pageSize,
            });
            return response;
          } catch {
            return null;
          }
        },
        [isV2, accountId, initialSection.id, sectionConfiguration],
      );

      // Handle sync when user clicks notification
      const handleSyncWithApiData = useCallback(() => {
        if (apiDataRef) {
          // Check if API data has an error status
          if (apiDataRef.status === "failed") {
            // Update to error state when user explicitly syncs
            setHasError(true);
            setIsStale(false);
            setReportSectionData(apiDataRef);
          } else {
            // Update displayed data with latest API data
            setReportSectionData(apiDataRef);
            setIsStale(false);
            setHasError(false);
          }
          // Hide notification
          setShowDataUpdateNotification(false);
          // Clear API data reference
          setApiDataRef(null);
        }
      }, [apiDataRef]);

      // Handle dismiss notification
      const handleDismissNotification = useCallback(() => {
        setShowDataUpdateNotification(false);
        setApiDataRef(null);
      }, []);

      const setDataConfigurations = useGlobalStore(
        (state) => state.setDataConfigurations,
      );

      const handleLabelDelete = async (labelId: string) => {
        try {
          if (accountId) {
            await deleteLabelV2(accountId, labelId);

            // Remove sections that use this label BEFORE refreshing configs
            // to prevent stale group_by values from triggering failed API calls
            if (removeSectionOnDeletingLabel) removeSectionOnDeletingLabel(labelId);

            // Cancel any in-flight requests for this section since it may be unmounting
            if (cancelTokenSourceRef.current) {
              cancelTokenSourceRef.current.cancel("Label deleted");
              cancelTokenSourceRef.current = null;
            }
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }

            const res = await getDataConfigurations(accountId);
            setDataConfigurations(res.configuration);

            toast.message(
              <ToastContent
                headingPrefix="Success!"
                subheadingSuffix="Label deleted successfully."
              />,
            );
          }
        } catch (error) {
          console.error(error);
        }
      };

      const sectionInfo =
        dataConfigurations?.section_types[
          sectionConfiguration?.section_type ?? "regular"
        ];
      const isSequenceLabelView =
        (sectionConfiguration.dropdowns?.["label_view"] as string) === "sequence";

      // enabled keys
      const enabledKeys = sectionInfo?.enabled_keys;

      // Media Filter States
      const selectedGroupByValue = sectionConfiguration.dropdowns?.["group_by"] as string;

      const selectedGroupByOption = dataConfigurations?.dropdowns
        ?.find((d) => d.key === "group_by")
        ?.options?.find((op) => op.key === selectedGroupByValue);

      const labelsMedia =
        selectedGroupByOption?.creative_type === "image" ||
        selectedGroupByOption?.creative_type === "video";

      // Media type (image/video) comes from group_by option for label types, or from filters
      const mediaFilterValue = labelsMedia
        ? selectedGroupByOption?.creative_type || null
        : null;

      // metrics state
      const [showAddMetrics, setShowAddMetrics] = useState<boolean>(false);

      // Update filter labels from latest dataConfigurations to handle label changes
      const selectedFiltersWithLatestLabels = useMemo(() => {
        const selectedFilters = sectionConfiguration.filters ?? [];
        if (!selectedFilters || selectedFilters.length === 0) return selectedFilters;
        if (!dataConfigurations?.filter?.filter_choices) return selectedFilters;

        return selectedFilters.map((filter) => {
          // Find the latest filter choice from dataConfigurations
          const latestFilterChoice = dataConfigurations.filter.filter_choices.find(
            (choice) => choice.key === filter.key,
          );

          // Update filter label if found
          const updatedFilter: SectionFilter = {
            ...filter,
            label: latestFilterChoice?.label ?? filter.label,
          };

          // Update options if they exist
          if (filter.options && latestFilterChoice?.options) {
            updatedFilter.options = filter.options.map((option) => {
              const latestOption = latestFilterChoice.options?.find(
                (opt) => opt.key === option.key,
              );
              return {
                ...option,
                label: latestOption?.label ?? option.label,
              };
            });
          }

          // Update conditions labels
          if (filter.conditions && filter.conditions.length > 0) {
            updatedFilter.conditions = filter.conditions.map((condition) => {
              // Find the filter choice for this condition
              const conditionFilterChoice = dataConfigurations.filter.filter_choices.find(
                (choice) => choice.key === condition.key,
              );

              // If not found, look in children (for nested filters like AI labels)
              if (!conditionFilterChoice) {
                const parentChoice = dataConfigurations.filter.filter_choices.find(
                  (choice) =>
                    choice.children?.some((child) => child.key === condition.key),
                );
                const childChoice = parentChoice?.children?.find(
                  (child) => child.key === condition.key,
                );
                // Only use label from child choice, don't assign the whole object
                const updatedCondition = {
                  ...condition,
                  label: childChoice?.label ?? condition.label,
                };
                return updatedCondition;
              }

              const updatedCondition = {
                ...condition,
                label: conditionFilterChoice?.label ?? condition.label,
              };

              // Update condition value labels if it's an option type
              if (
                conditionFilterChoice?.filter_type === "option" &&
                conditionFilterChoice?.options &&
                typeof condition.value === "string"
              ) {
                const matchingOption = conditionFilterChoice.options.find(
                  (opt) => opt.key === condition.value,
                );
                if (matchingOption) {
                  // Keep the value as key, but we've updated the condition label
                  // The display will use the latest options from dataConfigurations
                }
              }

              return updatedCondition;
            });
          }

          // Update children if they exist
          if (
            filter.children &&
            Array.isArray(filter.children) &&
            filter.children.length > 0
          ) {
            updatedFilter.children = filter.children.map((child) => {
              // Find the child filter choice
              const parentChoice = dataConfigurations.filter.filter_choices.find(
                (choice) => choice.children?.some((c) => c.key === child.key),
              );
              const childFilterChoice = parentChoice?.children?.find(
                (c) => c.key === child.key,
              );

              const updatedChild = {
                ...child,
                label: childFilterChoice?.label ?? child.label,
              };

              // Update child conditions
              if (
                child.conditions &&
                Array.isArray(child.conditions) &&
                child.conditions.length > 0
              ) {
                updatedChild.conditions = child.conditions.map((condition) => {
                  const conditionFilterChoice =
                    dataConfigurations.filter.filter_choices.find(
                      (choice) => choice.key === condition.key,
                    );

                  if (!conditionFilterChoice) {
                    const parentChoice = dataConfigurations.filter.filter_choices.find(
                      (choice) => choice.children?.some((c) => c.key === condition.key),
                    );
                    const childChoice = parentChoice?.children?.find(
                      (c) => c.key === condition.key,
                    );
                    return {
                      ...condition,
                      label: childChoice?.label ?? condition.label,
                    };
                  }

                  return {
                    ...condition,
                    label: conditionFilterChoice?.label ?? condition.label,
                  };
                });
              }

              // Update child options
              if (
                child.options &&
                Array.isArray(child.options) &&
                childFilterChoice?.options
              ) {
                updatedChild.options = child.options.map((option) => {
                  const latestOption = childFilterChoice.options?.find(
                    (opt) => opt.key === option.key,
                  );
                  return {
                    ...option,
                    label: latestOption?.label ?? option.label,
                  };
                });
              }

              return updatedChild;
            }) as SectionFilter["children"];
          }

          return updatedFilter;
        });
      }, [sectionConfiguration.filters, dataConfigurations?.filter?.filter_choices]);

      // label view dropdown
      const labelViewDropdown = dataConfigurations?.dropdowns?.find(
        (d) => d.key === "label_view",
      );
      const labelViewOptions = labelViewDropdown?.options.map((o) => ({
        key: o.key,
        label: o.label,
        info: o.info,
      }));

      const labelViewValue = sectionConfiguration.dropdowns?.["label_view"];
      const labelViewMeetsDependencies = useMemo(() => {
        if (!labelViewDropdown) return false;
        return dropdownDependenciesCheck(
          labelViewDropdown,
          sectionConfiguration.dropdowns ?? {},
          dataConfigurations?.dropdowns ?? [],
        );
      }, [
        labelViewDropdown,
        sectionConfiguration.dropdowns,
        dataConfigurations?.dropdowns,
      ]);

      // clear label view when dependencies are unmet
      useEffect(() => {
        if (!labelViewMeetsDependencies && labelViewValue) {
          const dropdownsCopy = {
            ...(sectionConfiguration.dropdowns ?? {}),
          } as Record<string, unknown>;
          if ("label_view" in dropdownsCopy) {
            delete dropdownsCopy.label_view;
            handleConfigurationChange(
              {
                dropdowns: dropdownsCopy as typeof sectionConfiguration.dropdowns,
              },
              "dropdowns",
            );
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [labelViewMeetsDependencies]);

      // group by dropdown
      const groupByDropdown = dataConfigurations?.dropdowns?.find(
        (d) => d.key === "group_by",
      );
      const groupByOptions: GroupByOption[] =
        groupByDropdown?.options.map((o) => ({
          key: o.key,
          label: o.label,
          type: o.column_type,
          creative_type: o.creative_type,
          show_platforms: !!o?.show_platforms,
          platforms: o?.platforms ?? [],
          info: o?.info,
          supported_breakdowns: o?.supported_breakdowns,
        })) || [];
      const groupByValue = sectionConfiguration.dropdowns?.["group_by"] as string;

      const supportedBreakdownKeys =
        groupByOptions.find((g) => g.key === groupByValue)?.supported_breakdowns || [];

      // breakdown state
      const breakdownOptions = dataConfigurations?.dropdowns
        ?.find((d) => d.key === "breakdown_by")
        ?.options.map((o) => ({
          key: o.key,
          label: o.label,
          show_platforms: !!o?.show_platforms,
          platforms: o?.platforms ?? [],
        }))
        .filter((o) => supportedBreakdownKeys.includes(o.key));

      const isBreakdownHidden = useMemo(() => {
        return (
          viewMode === "creative_view" ||
          viewMode === "creative_share_view" ||
          viewMode === "line_chart_view"
        );
      }, [viewMode]);

      const breakdownValue: string | null =
        supportedBreakdownKeys.includes(
          sectionConfiguration.dropdowns?.["breakdown_by"] as string,
        ) && typeof sectionConfiguration.dropdowns?.["breakdown_by"] === "string"
          ? sectionConfiguration.dropdowns?.["breakdown_by"]
          : null;

      const [searchParams, setSearchParams] = useSearchParams();
      const handleCreateLabel = () => {
        const params = new URLSearchParams(searchParams);
        params.set("type", "label");
        params.set("apply", "true");
        setSearchParams(params);
      };

      // Memoize all computed props to prevent unnecessary re-renders of child components
      const metricOptions = useMemo(
        () =>
          dataConfigurations?.column_choices?.find((c) => c.key === "metric")?.options ??
          [],
        [dataConfigurations?.column_choices],
      );

      const selectedMetricsValues = sectionConfiguration.column_choices?.["metric"];

      const selectedMetrics = useMemo(
        () =>
          selectedMetricsValues
            ?.map((metricKey) => metricOptions?.find((m) => m.key === metricKey))
            .filter((metric): metric is ColumnOption => metric !== undefined) ?? [],
        [selectedMetricsValues, metricOptions],
      );

      // Memoized metric labels for Ask AI attachment display
      const selectedMetricLabels = useMemo(
        () => selectedMetrics.map((m) => ({ id: m.key, label: m.label })),
        [selectedMetrics],
      );

      // Sync section metrics to attached creatives store when they change
      const setCurrentSectionMetrics = useAttachedCreativesStore(
        (s) => s.setCurrentSectionMetrics,
      );
      useEffect(() => {
        setCurrentSectionMetrics(initialSection.id, selectedMetricLabels);
      }, [selectedMetricLabels, initialSection.id, setCurrentSectionMetrics]);

      const availableMetricsOptions = useMemo(() => {
        const metricOrder = sectionConfiguration.column_choices?.metric || [];
        if (metricOrder.length === 0) return [];

        // Create a map for O(1) lookup
        const metricOptionsMap = new Map(
          metricOptions.map((option) => [option.key, option]),
        );

        // Maintain the order from sectionConfiguration.column_choices.metric
        return metricOrder
          .map((key) => metricOptionsMap.get(key))
          .filter((option): option is NonNullable<typeof option> => option !== undefined);
      }, [metricOptions, sectionConfiguration.column_choices?.metric]);

      // Filter metrics for Creative Share Chart based on supported_views
      const creativeShareMetricsOptions = useMemo(
        () =>
          metricOptions.filter((o) => {
            const supportedViews = o.supported_views;
            return (
              Array.isArray(supportedViews) &&
              supportedViews.includes("creative_share_view")
            );
          }),
        [metricOptions],
      );

      const allMetricsOptionsMap = useMemo(
        () =>
          metricOptions.reduce<Record<string, ColumnOption>>((acc, curr) => {
            acc[curr.key] = curr;
            return acc;
          }, {}),
        [metricOptions],
      );

      const dataColumns = useMemo(
        () =>
          reportSectionData?.data?.columns
            .filter((c) => c.hidden_from_table !== true)
            .map((c) => {
              if (!allMetricsOptionsMap[c.key]) return c;
              const metricFormula = allMetricsOptionsMap[c.key]?.metric_formula ?? null;
              return {
                ...c,
                display_formula: getMetricFormulaLabel(metricFormula, metricOptions),
              };
            }) ?? [],
        [reportSectionData?.data?.columns, allMetricsOptionsMap, metricOptions],
      );

      const groupType = useMemo(
        () =>
          dataConfigurations?.dropdowns
            ?.find((d) => d.key === "group_by")
            ?.options?.find(
              (choice) => choice.key === sectionConfiguration.dropdowns?.["group_by"],
            )?.column_type,
        [dataConfigurations?.dropdowns, sectionConfiguration.dropdowns],
      );

      const groupByKey = useMemo(
        () => sectionConfiguration.dropdowns?.["group_by"] as string | undefined,
        [sectionConfiguration.dropdowns],
      );

      // Sync section groupBy to attached creatives store when it changes
      const setCurrentSectionGroupBy = useAttachedCreativesStore(
        (s) => s.setCurrentSectionGroupBy,
      );
      useEffect(() => {
        setCurrentSectionGroupBy(initialSection.id, groupByKey || null);
      }, [groupByKey, initialSection.id, setCurrentSectionGroupBy]);

      const labelViewString = useMemo(
        () => (sectionConfiguration.dropdowns?.["label_view"] as string) ?? undefined,
        [sectionConfiguration.dropdowns],
      );

      const labelConfigIdMemoized = useMemo(
        () =>
          Array.isArray(sectionConfiguration.dropdowns?.["group_by"])
            ? sectionConfiguration.dropdowns?.["group_by"][0]
            : (sectionConfiguration.dropdowns?.["group_by"] ?? undefined),
        [sectionConfiguration.dropdowns],
      );

      const showAddMetricButtonMemoized = useMemo(
        () => enabledKeys?.includes("column_choices"),
        [enabledKeys],
      );

      const mediaFilterValueMemoized = useMemo(
        () => mediaFilterValue,
        [mediaFilterValue],
      );

      // Memoize insights array to prevent re-renders
      const insightsMemoized = useMemo(() => insights || [], [insights]);

      // Sort data for grid view to match table view sorting (skip for v2 — already server-sorted)
      const sortedGridData = useMemo(() => {
        const rows = reportSectionData?.data?.rows;
        if (!rows || !Array.isArray(rows)) return rows || [];

        if (isV2) return rows;

        const sortState = sectionConfiguration.additional_config?.sort_state;
        if (!sortState || !sortState.column || sortState.direction === false) {
          return rows;
        }

        // Find the column being sorted
        const sortColumn = dataColumns.find((col) => col.key === sortState.column);

        // For node_group columns, use default_key instead (matching table behavior)
        const sortColumnKey =
          sortColumn && (sortColumn.column_type as string) === "node_group"
            ? sortColumn.default_key || sortState.column
            : sortState.column;

        // Create a copy to avoid mutating the original array
        const sorted = [...rows];

        // Helper function to check if a value is N/A
        const isNAValue = (val: unknown) => {
          const str = String(val);
          return str === "N/A" || str === "n/a" || str === "";
        };

        // Helper function to get sortable value for text_ai_label arrays
        const getSortableValue = (val: unknown): unknown => {
          if (Array.isArray(val)) {
            try {
              // For text_ai_label arrays, concatenate labels without separators
              return val.reduce((acc: string, curr) => {
                // Check if current item is an object with a label property
                if (
                  curr &&
                  typeof curr === "object" &&
                  "label" in curr &&
                  typeof (curr as { label: string }).label === "string"
                ) {
                  return acc + (curr as { label: string }).label;
                }
                // If not a valid object with label, try to convert to string as fallback
                return acc + String(curr || "");
              }, "");
            } catch (error) {
              // If any error occurs during processing, return the original value
              console.warn("Error processing array for sorting:", error);
              return String(val);
            }
          }
          return val;
        };

        sorted.sort((rowA, rowB) => {
          const aValue = (rowA as Record<string, unknown>)[sortColumnKey];
          const bValue = (rowB as Record<string, unknown>)[sortColumnKey];

          const aSortable = getSortableValue(aValue);
          const bSortable = getSortableValue(bValue);

          const aIsNA = isNAValue(aSortable);
          const bIsNA = isNAValue(bSortable);

          // If both are N/A, they are equal
          if (aIsNA && bIsNA) return 0;

          // Handle N/A values based on sort direction
          if (aIsNA && !bIsNA) {
            // A is N/A, B is not - A should go to bottom
            return sortState.direction === "desc" ? -1 : 1;
          }

          if (!aIsNA && bIsNA) {
            // B is N/A, A is not - B should go to bottom
            return sortState.direction === "desc" ? 1 : -1;
          }

          // Neither is N/A, perform normal comparison
          const aNum = Number(aSortable);
          const bNum = Number(bSortable);

          if (!isNaN(aNum) && !isNaN(bNum)) {
            const result = aNum - bNum;
            return sortState.direction === "desc" ? -result : result;
          }

          // String comparison
          const result = String(aSortable).localeCompare(String(bSortable));
          return sortState.direction === "desc" ? -result : result;
        });

        return sorted;
      }, [
        reportSectionData?.data?.rows,
        sectionConfiguration.additional_config?.sort_state,
        dataColumns,
        isV2,
      ]);

      // Memoize graph headline to prevent re-renders
      const graphHeadline = useMemo(
        () =>
          `${
            dataConfigurations?.dropdowns
              ?.find((d) => d.key === "group_by")
              ?.options.find(
                (choice) => choice.key === sectionConfiguration.dropdowns?.["group_by"],
              )?.label || "Performance"
          } Trend`,
        [dataConfigurations?.dropdowns, sectionConfiguration.dropdowns],
      );

      const graphConfigWithReportMetadata = useMemo(
        () => ({
          ...sectionConfiguration,
          dropdowns: {
            ...sectionConfiguration.dropdowns,
            breakdown_by: null,
          },
        }),
        [sectionConfiguration],
      );

      const handleMetricsChangeMemoized = useCallback(
        (metrics: ColumnOption[]) => {
          const newMetrics = metrics.map((m) => m.key);
          const prevMetrics = sectionConfiguration.column_choices?.["metric"] || [];

          // Check if it's just a reorder (same metrics, different order)
          const prevMetricsSet = new Set(prevMetrics);
          const newMetricsSet = new Set(newMetrics);
          const isReorder =
            prevMetrics.length === newMetrics.length &&
            prevMetricsSet.size === newMetricsSet.size &&
            [...prevMetricsSet].every((m) => newMetricsSet.has(m)) &&
            JSON.stringify(prevMetrics) !== JSON.stringify(newMetrics);
          const newConfiguration: CreativeSectionConfiguration = {
            ...sectionConfiguration,
            column_choices: {
              metric: newMetrics,
            },
          };
          if (isReorder)
            // For reorder: update config only without refetching data
            handleConfigOnlyUpdate(newConfiguration);
          else
            // For add/remove: trigger full config change (will refetch data)
            handleConfigurationChange(
              {
                column_choices: {
                  metric: newMetrics,
                },
              },
              "column_choices",
            );
        },
        [sectionConfiguration, handleConfigOnlyUpdate, handleConfigurationChange],
      );

      // Convert section configuration date to CreativeReportMetadata format for date selector
      const sectionDateMetadata = useMemo((): CreativeReportMetadata | null => {
        if (!sectionConfiguration.date) return null;
        return {
          date: sectionConfiguration.date,
          compare_date: sectionConfiguration.compare_date ?? null,
          report_filters: [],
        };
      }, [sectionConfiguration.date, sectionConfiguration.compare_date]);

      // Handle date change from date selector
      const handleDateChange = useCallback(
        (metadata: CreativeReportMetadata | null, applyToAllSections?: boolean) => {
          if (!metadata?.date) return;

          handleConfigurationChange(
            {
              date: metadata.date,
              compare_date: metadata.compare_date ?? null,
            },
            undefined, // No specific key for date changes
            applyToAllSections,
          );
        },
        [handleConfigurationChange],
      );

      // Use fetched section for display when available; merge title/icon from initialSection
      // so parent-driven updates (e.g. user title change) are reflected
      const displaySection = fetchedSection
        ? {
            ...fetchedSection,
            title: initialSection.title,
            icon: initialSection.icon ?? fetchedSection.icon,
          }
        : initialSection;

      if (sectionConfigLoading) {
        return (
          <div
            data-section-id={initialSection?.id || ""}
            className="w-full h-full flex justify-center items-center"
          >
            <PageLoader />
          </div>
        );
      }

      return (
        <div
          data-section-id={initialSection?.id || ""}
          className="py-[51px] border-border relative"
        >
          {/* Data Update Notification */}
          {showDataUpdateNotification && (
            <div
              className="fixed top-[6px] left-1/2 -translate-x-1/2"
              style={{ zIndex: 1000 }}
            >
              <ToastNotification
                variant={apiDataRef?.status === "failed" ? "error" : "sync"}
                onActionClick={
                  apiDataRef?.status === "failed" ? onRetry : handleSyncWithApiData
                }
                onDismissClick={handleDismissNotification}
              />
            </div>
          )}
          <div className="sticky top-0 z-[12] bg-background-gray pb-1 mb-1">
            {/* Section Title */}
            <div className="flex gap-2">
              {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}

              {reportType === ReportType.Regular ? (
                <CreativeReportTitle
                  data={{
                    title: displaySection?.title || "",
                    icon: displaySection?.icon ?? "",
                  }}
                  setData={(data) => onTitleChange(initialSection.id, data, true)}
                  titlePlaceholder={displaySection?.title || ""}
                />
              ) : (
                <ReportTitle
                  title={displaySection?.title || ""}
                  icon={
                    creativeSidebarItems[reportType] &&
                    createElement(creativeSidebarItems[reportType].icon, {
                      className: "w-[18px] h-[18px] text-brand-orange",
                    })
                  }
                  iconBackgroundColor="#FF45000F"
                />
              )}
            </div>
            <div className="mt-4 flex gap-[6px] justify-between flex-wrap mb-2">
              <div className="flex gap-[6px] flex-wrap">
                <CreativeReportDateSelector
                  selectedFilters={sectionDateMetadata}
                  setSelectedFilters={(metadata) => handleDateChange(metadata, false)}
                  first_allowed_date={allowedDateRange?.first_allowed_date}
                  last_allowed_date={allowedDateRange?.last_allowed_date}
                  timezone={timezone}
                  enable_compare
                  disabled={sectionConfiguration.section_type === "fatigue"}
                  onApplyToAllSections={(metadata) => handleDateChange(metadata, true)}
                  onNewDateAvailable={onNewDateAvailable}
                />

                {sectionInfo?.label && <SectionLabel sectionLabel={sectionInfo.label} />}
                {enabledKeys?.includes("dropdowns.group_by") && (
                  <GroupBy
                    options={groupByOptions}
                    value={groupByValue}
                    onChange={(value: string) => {
                      handleConfigurationChange(
                        {
                          dropdowns: {
                            ...sectionConfiguration.dropdowns,
                            group_by: value,
                            breakdown_by:
                              groupByOptions
                                .find((g) => g.key === value)
                                ?.supported_breakdowns?.includes(
                                  sectionConfiguration.dropdowns?.[
                                    "breakdown_by"
                                  ] as string,
                                ) &&
                              typeof sectionConfiguration.dropdowns?.["breakdown_by"] ===
                                "string"
                                ? sectionConfiguration.dropdowns?.["breakdown_by"]
                                : null,
                          },
                        },
                        "dropdowns",
                      );
                    }}
                    handleLabelDelete={handleLabelDelete}
                    accountId={accountId ?? ""}
                    sectionId={initialSection?.id || ""}
                    handleCreateLabel={handleCreateLabel}
                  />
                )}
                {enabledKeys?.includes("filter") && dataConfigurations?.filter && (
                  <Filters
                    selectedFilters={selectedFiltersWithLatestLabels}
                    onFilterChange={(filters) => {
                      handleConfigurationChange(
                        {
                          filters: filters ?? [],
                        },
                        "filters",
                      );
                    }}
                    selectedDate={sectionConfiguration.date ?? null}
                    dataConfigFilter={dataConfigurations?.filter || {}}
                    columnChoices={dataConfigurations?.column_choices}
                    showSelectedFilters={false}
                    showAdvancedFilter={showAdvancedFilter}
                    onShowAdvancedFilterChange={setShowAdvancedFilter}
                    advancedTempFilter={advancedTempFilter}
                    onAdvancedTempFilterChange={setAdvancedTempFilter}
                    advancedOpenedAtRef={advancedOpenedAtRef}
                    customTrigger={
                      <FiltersCustomTrigger
                        active={selectedFiltersWithLatestLabels.length > 0}
                        tooltipText="Section filters"
                      />
                    }
                    clearSelection
                  />
                )}
                {enabledKeys?.includes("dropdowns.breakdown_by") &&
                  breakdownOptions?.length && (
                    <BreakdownBy
                      options={breakdownOptions}
                      hidden={isBreakdownHidden}
                      value={breakdownValue}
                      onChange={(value) => {
                        handleConfigurationChange(
                          {
                            dropdowns: {
                              ...sectionConfiguration.dropdowns,
                              breakdown_by: value,
                            },
                          },
                          "dropdowns",
                        );
                      }}
                    />
                  )}

                {enabledKeys?.includes("dropdowns.label_view") &&
                  labelViewMeetsDependencies &&
                  labelViewOptions?.length && (
                    <LabelView
                      creativeType={mediaFilterValue}
                      options={labelViewOptions}
                      value={labelViewValue as string}
                      onChange={(value) => {
                        handleConfigurationChange(
                          {
                            dropdowns: {
                              ...sectionConfiguration.dropdowns,
                              label_view: value,
                            },
                          },
                          "dropdowns",
                        );
                      }}
                      viewMode={viewMode}
                    />
                  )}
              </div>
              <div className="flex gap-[6px]">
                {enabledKeys?.includes("column_choices") &&
                  viewMode !== "line_chart_view" &&
                  viewMode !== "bar_chart_view" &&
                  viewMode !== "creative_share_view" && (
                    <EditMetricsToggle
                      active={showAddMetrics}
                      onToggle={() => setShowAddMetrics((i) => !i)}
                    />
                  )}
                <ViewModeDropdown
                  view={viewMode}
                  onViewChange={handleViewMode}
                  metadata={{
                    line_chart_view: {
                      disabled: isSequenceLabelView,
                      disablingReason: isSequenceLabelView
                        ? "Graph isn't supported in Sequence. Switch to Multiple to view graph."
                        : undefined,
                    },
                    creative_share_view: {
                      disabled:
                        isSequenceLabelView ||
                        sectionConfiguration.section_type === "fatigue",
                      disablingReason: isSequenceLabelView
                        ? "Creative Share isn't supported in Sequence. Switch to Multiple to view Creative Share."
                        : undefined,
                    },
                    creative_view: {
                      disabled: sectionConfiguration.section_type === "fatigue",
                    },
                  }}
                />
              </div>
            </div>
            {/* Filters */}
            {(selectedFiltersWithLatestLabels.length > 0 || showAdvancedFilter) && (
              <div className="mb-2">
                {dataConfigurations?.filter &&
                  sectionInfo?.enabled_keys.includes("filter") && (
                    <Filters
                      selectedFilters={selectedFiltersWithLatestLabels}
                      onFilterChange={(filters) => {
                        handleConfigurationChange(
                          {
                            filters: filters ?? [],
                          },
                          "filters",
                        );
                      }}
                      selectedDate={sectionConfiguration.date ?? null}
                      dataConfigFilter={dataConfigurations?.filter || {}}
                      columnChoices={dataConfigurations?.column_choices}
                      showAddFilters={false}
                      showAdvancedFilter={showAdvancedFilter}
                      onShowAdvancedFilterChange={setShowAdvancedFilter}
                      advancedTempFilter={advancedTempFilter}
                      onAdvancedTempFilterChange={setAdvancedTempFilter}
                      advancedOpenedAtRef={advancedOpenedAtRef}
                    />
                  )}
              </div>
            )}
            {/* metrics */}
            {enabledKeys?.includes("column_choices") &&
              showAddMetrics &&
              viewMode !== "line_chart_view" &&
              viewMode !== "bar_chart_view" &&
              viewMode !== "creative_share_view" && (
                <Metrics
                  metricsOptions={metricOptions}
                  selectedMetrics={selectedMetrics}
                  onMetricChange={handleMetricsChangeMemoized}
                  className="mb-1"
                />
              )}
          </div>
          {/* Section Data */}
          <div
            className={cn(
              "block",
              ((loadingData && reportSectionData?.status !== "partial") ||
                reportSectionData?.status === "pending" ||
                reportSectionData?.status === "failed" ||
                hasError ||
                !reportSectionData?.data ||
                reportSectionData?.data?.rows?.length === 0) &&
                "hidden",
            )}
          >
            {viewMode === "line_chart_view" && (
              <CreativePerformanceGraph
                accountId={accountId}
                sectionId={initialSection?.id || ""}
                availableMetrics={availableMetricsOptions}
                initialConfig={sectionConfiguration.additional_config?.graph_config}
                onConfigChange={handleGraphConfigChange}
                configWithReportMetadata={graphConfigWithReportMetadata}
              />
            )}
            {viewMode === "creative_share_view" && (
              <CreativeShareChart
                headline={graphHeadline}
                accountId={accountId}
                sectionId={initialSection?.id || ""}
                availableMetrics={creativeShareMetricsOptions}
                initialConfig={sectionConfiguration.additional_config?.graph_config}
                onConfigChange={handleGraphConfigChange}
                configWithReportMetadata={graphConfigWithReportMetadata}
                primaryKeyToCreativeMap={primaryKeyToCreativeMap}
              />
            )}
          </div>
          <div>
            {loaderCompleting ? (
              <div className="my-[250px] w-full flex justify-center items-center">
                <ReportLoader showProgressBar message={pendingMessage} completing />
              </div>
            ) : (loadingData || isStale) &&
              reportSectionData?.status !== "partial" &&
              !hasError ? (
              <div className="my-[250px] w-full flex justify-center items-center">
                <PageLoader />
              </div>
            ) : reportSectionData?.status === "pending" &&
              !hasError &&
              reportSectionData?.message ? (
              <div className="my-[250px] w-full flex justify-center items-center">
                <ReportLoader
                  showProgressBar={loadingData || reportSectionData?.status === "pending"}
                  message={pendingMessage}
                  pending_count={reportSectionData?.sync_details?.pending_count}
                  total_count={reportSectionData?.sync_details?.total_count}
                />
              </div>
            ) : reportSectionData?.status === "pending" && !hasError ? (
              <div className="my-[250px] w-full flex justify-center items-center">
                <PageLoader />
              </div>
            ) : reportSectionData?.status === "failed" ? (
              <div className="mt-[121px] mb-[34px] w-full flex justify-center items-center">
                <APIErrorCard
                  onRetry={onRetry}
                  message={reportSectionData.message}
                  messageTitle={reportSectionData.message_title}
                />
              </div>
            ) : hasError ? (
              <div className="my-[250px] w-full flex flex-col justify-center items-center">
                <div className="text-destructive text-lg font-medium mb-2">
                  Error loading data
                </div>
                <div className="text-muted-foreground text-sm mb-4">
                  Failed to fetch data for this section. Please try again.
                </div>
                <Button variant="outline" onClick={onRetry}>
                  Retry
                </Button>
              </div>
            ) : !reportSectionData?.data || reportSectionData?.data?.rows.length === 0 ? (
              <div className="my-[250px] w-full h-1/2">
                <NoDataCard />
              </div>
            ) : (
              <div>
                {/* Conditional rendering based on viewMode with smooth transitions */}
                {reportSectionData?.data && reportSectionData?.data.columns && (
                  <div className="w-full">
                    {/* Table/Graph/Bar/Creative Share View Container */}
                    <div
                      className={`w-full transition-all duration-300 ease-in-out ${
                        viewMode === "table_view" ||
                        viewMode === "line_chart_view" ||
                        viewMode === "bar_chart_view" ||
                        viewMode === "creative_share_view"
                          ? "block"
                          : "hidden"
                      }`}
                    >
                      {viewMode === "bar_chart_view" && (
                        <CreativePerformanceBarChart
                          rows={reportSectionData?.data.rows || []}
                          columns={dataColumns}
                          availableMetrics={availableMetricsOptions}
                          initialConfig={
                            sectionConfiguration.additional_config?.graph_config
                          }
                          onConfigChange={handleBarConfigChange}
                          metricOptions={metricOptions}
                          sectionId={initialSection?.id || ""}
                          groupByKey={groupByKey}
                          barChartPageSize={barChartPageSize}
                        />
                      )}

                      {(viewMode === "line_chart_view" ||
                        viewMode === "bar_chart_view" ||
                        viewMode === "creative_share_view") && (
                        <div className="mt-4 mb-3">
                          <div className=" pl-3 flex items-center justify-between mb-3">
                            <h3
                              className="truncate text-[14px] font-medium leading-[30.651px] text-brand-primary-800 tracking-[0.28px] not-italic"
                              style={{ textOverflow: "ellipsis" }}
                            >
                              Source Table
                            </h3>
                            {enabledKeys?.includes("column_choices") && (
                              <EditMetricsToggle
                                active={showAddMetrics}
                                onToggle={() => setShowAddMetrics((i) => !i)}
                              />
                            )}
                          </div>
                          {enabledKeys?.includes("column_choices") && showAddMetrics && (
                            <Metrics
                              metricsOptions={metricOptions}
                              selectedMetrics={selectedMetrics}
                              onMetricChange={handleMetricsChangeMemoized}
                              className="mb-1"
                            />
                          )}
                        </div>
                      )}
                      <CreativeTable
                        data={reportSectionData?.data.rows}
                        columns={dataColumns}
                        ad_columns={reportSectionData?.data?.ad_columns}
                        breakdown_columns={reportSectionData?.data?.breakdown_columns}
                        setGroupSheetOpen={setGroupSheetOpen}
                        setSelectedAdGroup={setSelectedAdGroup}
                        onOpenGroupSheetWithMode={handleOpenGroupSheetWithMode}
                        totalRow={reportSectionData?.data.total}
                        uniqueLabels={reportSectionData.data.unique_labels}
                        status={reportSectionData?.status}
                        partialColumns={reportSectionData?.data.partial_columns}
                        preservePaginationState={true}
                        initialPageSize={
                          viewMode === "bar_chart_view"
                            ? barChartPageSize
                            : viewMode === "line_chart_view"
                              ? lineChartPageSize
                              : viewMode === "creative_share_view"
                                ? 10
                                : undefined
                        }
                        hidePageSizeSelector={viewMode === "bar_chart_view"}
                        initialColumnSizing={
                          sectionConfiguration.additional_config?.column_configuration
                        }
                        onColumnResize={handleColumnResize}
                        onSort={handleColumnSort}
                        initialSortState={
                          sectionConfiguration.additional_config?.sort_state
                        }
                        viewMode={
                          viewMode === "bar_chart_view" ||
                          viewMode === "creative_share_view"
                            ? "table_view"
                            : viewMode
                        }
                        sectionId={initialSection.id}
                        groupType={groupType}
                        creativeType={mediaFilterValueMemoized ?? undefined}
                        labelView={labelViewString}
                        labelConfigId={labelConfigIdMemoized}
                        accountId={accountId}
                        reportMetadata={sectionDateMetadata}
                        // Metrics functionality
                        metricOptions={metricOptions}
                        selectedMetrics={selectedMetrics}
                        onMetricChange={handleMetricsChangeMemoized}
                        showAddMetricButton={showAddMetricButtonMemoized}
                        groupByKey={groupByKey}
                        sectionMetricLabels={selectedMetricLabels}
                        breakdownValue={breakdownValue}
                        hideBreakdownRows={isBreakdownHidden}
                        serverPagination={isV2 ? serverPagination : undefined}
                        onServerPageChange={isV2 ? handleServerPageChange : undefined}
                        onServerPageSizeChange={
                          isV2 ? handleServerPageSizeChange : undefined
                        }
                        isServerSorted={isV2}
                        isPaginationLoading={isPaginationLoading}
                        fetchGalleryAds={isV2 ? handleFetchGalleryAds : undefined}
                      />
                    </div>

                    {/* Grid View Container */}
                    <div
                      className={`w-full transition-all duration-300 ease-in-out ${
                        viewMode === "creative_view" ? "block" : "hidden"
                      }`}
                    >
                      <CreativeCardGrid
                        key={viewMode}
                        data={sortedGridData}
                        columns={dataColumns}
                        ad_columns={reportSectionData?.data?.ad_columns}
                        setGroupSheetOpen={setGroupSheetOpen}
                        sectionId={initialSection.id}
                        setSelectedAdGroup={setSelectedAdGroup}
                        uniqueLabels={reportSectionData?.data.unique_labels}
                        groupType={groupType}
                        groupByKey={groupByKey}
                        creativeType={mediaFilterValueMemoized ?? undefined}
                        labelView={labelViewString}
                        labelConfigId={labelConfigIdMemoized}
                        accountId={accountId}
                        sectionMetricLabels={selectedMetricLabels}
                        forcedAspectRatio="4:5"
                        serverPagination={isV2 ? serverPagination : undefined}
                        onServerPageChange={isV2 ? handleServerPageChange : undefined}
                        onServerPageSizeChange={
                          isV2 ? handleServerPageSizeChange : undefined
                        }
                        isPaginationLoading={isPaginationLoading}
                        fetchGalleryAds={isV2 ? handleFetchGalleryAds : undefined}
                      />
                    </div>
                  </div>
                )}
                {!isViewChanging && (
                  <ReportInsights
                    insights={insightsMemoized}
                    setInsights={setInsights}
                    type={InsightType.SectionInsights}
                    loading={insightsLoading}
                    generateInsight={handleGenerateInsightsForSection}
                    accountId={accountId}
                    initialInsight={
                      reportSectionData?.section?.insight || displaySection?.insight || ""
                    }
                    initialPrompt={
                      reportSectionData?.section?.insight_prompt ||
                      displaySection?.insight_prompt ||
                      ""
                    }
                    error={sectionConfiguration.error ?? ""}
                  />
                )}
              </div>
            )}
          </div>

          {/* Group Side Sheet */}
          <GroupSheet
            columns={reportSectionData?.data?.columns ?? []}
            ad_columns={reportSectionData?.data?.ad_columns ?? []}
            row={selectedAdGroup ?? null}
            initialViewMode={
              groupSheetViewMode ??
              (viewMode === "creative_view" ? "creative_view" : "table_view")
            }
            open={groupSheetOpen}
            setOpen={setGroupSheetOpen}
            sectionName={initialSection.title ?? ""}
            status={reportSectionData?.status ?? "success"}
            reportMetadata={sectionDateMetadata}
            uniqueLabels={reportSectionData?.data?.unique_labels}
            isV2={isV2}
            accountId={accountId}
            sectionId={initialSection.id}
            sectionConfiguration={sectionConfiguration}
            groupByKey={groupByKey}
          />
        </div>
      );
    },
  ),
  arePropsEqual, // Use custom comparison function
);

CreativeReportSection.displayName = "CreativeReportSection";
